'use client';

import React from 'react';
import { motion, Variants, useReducedMotion } from 'framer-motion';
import Image from 'next/image';
import { Cog, ShieldCheck, Thermometer } from 'lucide-react';
import { Heading, Badge } from '@/components/ui/design-system';

export function ManufacturingShowcase() {
  const [activeIndex, setActiveIndex] = React.useState(0);

  const tabRefs = React.useRef<HTMLButtonElement[]>([]);
  const prefersReducedMotion = useReducedMotion();

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: prefersReducedMotion ? {} : { staggerChildren: 0.2, delayChildren: 0.1 },
    },
  };

  const itemVariants: Variants = prefersReducedMotion
    ? {
        hidden: { opacity: 0, x: 0 },
        visible: { opacity: 1, x: 0, transition: { duration: 0.001 } },
      }
    : {
        hidden: { opacity: 0, x: 50 },
        visible: { 
          opacity: 1, 
          x: 0,
          transition: { duration: 0.5, ease: 'easeOut' }
        },
      };

  const features = [
    {
      title: 'Fully Automated Production Lines',
      description: 'Our 30,000m² factory is equipped with 36 fully automated production lines, all operated by robotic arms, running 24/7 to ensure stable production.',
      icon: <Cog className="h-16 w-16 text-primary-500" />,
      image: '/images/pages/home/manufacturing-showcase/automated-production-lines.jpg',
    },
    {
      title: 'Three-tier QC System',
      description: 'A rigorous, multi-stage quality control process ensures every product meets the highest standards of excellence and reliability.',
      icon: <ShieldCheck className="h-16 w-16 text-primary-500" />,
      image: '/images/pages/home/manufacturing-showcase/three-tier-qc-system.jpg',
    },
    {
      title: 'Precision Environmental Control',
      description: 'State-of-the-art climate and particle control systems maintain optimal conditions for sensitive manufacturing processes.',
      icon: <Thermometer className="h-16 w-16 text-primary-500" />,
      image: '/images/pages/home/manufacturing-showcase/precision-environmental-control.jpg',
    },
  ];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    let nextIndex: number | null = null;
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') nextIndex = (activeIndex + 1) % features.length;
    else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') nextIndex = (activeIndex - 1 + features.length) % features.length;
    else if (e.key === 'Home') nextIndex = 0;
    else if (e.key === 'End') nextIndex = features.length - 1;
    if (nextIndex !== null) {
      e.preventDefault();
      setActiveIndex(nextIndex);
      const btn = tabRefs.current[nextIndex];
      if (btn) btn.focus();
    }
  };

  const imageTransition = prefersReducedMotion ? { duration: 0.001 } : { duration: 0.5, ease: 'easeInOut' } as const;

  return (
    <section className="bg-white dark:bg-black py-16 sm:py-24 overflow-x-hidden">
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center justify-center space-y-4 text-center mb-12">
          <Badge variant="premium" size="lg">
            Our Process
          </Badge>
          <Heading 
            level={2} 
            id="manufacturing-showcase-heading"
            className="text-4xl font-bold leading-tight"
          >
            How Do We Achieve Lean Manufacturing?
          </Heading>         
        </div>
        {/* Desktop Layout: 2-column grid */}
        <div className="hidden lg:grid grid-cols-5 gap-12" aria-labelledby="manufacturing-showcase-heading">
          {/* Left Column */}
          {/* Left Column - Tab List */}
          <div className="flex flex-col col-span-2" role="tablist" aria-orientation="vertical" onKeyDown={handleKeyDown}>
            <motion.div 
              className="flex flex-col justify-between h-full gap-6"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              {features.map((feature, index) => (
                <motion.button
                  key={index}
                  id={`manufacturing-tab-${index}`}
                  role="tab"
                  type="button"
                  ref={(el) => { if (el) tabRefs.current[index] = el; }}
                  aria-controls={`manufacturing-panel-${index}`}
                  aria-selected={activeIndex === index}
                  tabIndex={activeIndex === index ? 0 : -1}
                  variants={itemVariants}
                  className={`p-6 border rounded-lg cursor-pointer transition-all duration-300 text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${activeIndex === index ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-900/20' : 'border-gray-200 dark:border-gray-800 hover:border-primary-300 hover:bg-gray-50/50 dark:hover:border-primary-700 dark:hover:bg-gray-900/20'}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onFocus={() => setActiveIndex(index)}
                >
                  <div className="flex items-start gap-6">
                    {feature.icon}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{feature.title}</h3>
                      <p className="mt-2 text-gray-600 dark:text-gray-400">{feature.description}</p>
                    </div>
                  </div>
                </motion.button>
              ))}
            </motion.div>
          </div>

          {/* Right Column - Tab Panels */}
          <div className="relative h-[550px] rounded-lg overflow-hidden col-span-3 z-10">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                id={`manufacturing-panel-${index}`}
                role="tabpanel"
                aria-labelledby={`manufacturing-tab-${index}`}
                hidden={activeIndex !== index}
                initial={{ opacity: 0 }}
                animate={{ opacity: activeIndex === index ? 1 : 0 }}
                transition={imageTransition}
                className="absolute inset-0"
              >
                <Image
                  src={feature.image}
                  alt={feature.title}
                  layout="fill"
                  objectFit="cover"
                  className="z-0"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent z-10"></div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Mobile & Tablet Layout: Interleaved cards and images */}
        <div className="lg:hidden space-y-12">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={itemVariants}
              className="space-y-6"
            >
              <div className="p-6 border rounded-lg border-gray-200 dark:border-gray-800">
                <div className="flex items-start gap-6">
                  {feature.icon}
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{feature.title}</h3>
                    <p className="mt-2 text-gray-600 dark:text-gray-400">{feature.description}</p>
                  </div>
                </div>
              </div>
              <div className="relative h-80 rounded-lg overflow-hidden">
                <Image
                  src={feature.image}
                  alt={feature.title}
                  layout="fill"
                  objectFit="cover"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}